import { useEffect, useState } from "react";
import { api } from "../api/client";
import { wsClient } from "../api/ws";

interface Signal {
  id: string;
  companyId: string;
  companyName?: string | null;
  title: string;
  description: string;
  kind: string;
  sourceKind: string;
  severity: string;
  status: string;
  occurrenceCount: number;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  createdAt?: string | null;
  templateName?: string | null;
  reportedByUserName?: string | null;
}

interface SignalsSummary {
  total?: number;
  newCount?: number;
  inProgressCount?: number;
  resolvedCount?: number;
  requestCount?: number;
  failureCount?: number;
  withAttachmentsCount?: number;
}

const SEV_CLASS: Record<string, string> = {
  critical: "pill-err",
  high: "pill-err",
  medium: "pill-warn",
  low: "",
};
const STATUS_CLASS: Record<string, string> = {
  new: "pill-warn",
  triaged: "",
  in_progress: "",
  resolved: "pill-ok",
  dismissed: "",
  duplicate: "",
};

export function Signals() {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [summary, setSummary] = useState<SignalsSummary | null>(null);
  const [env, setEnv] = useState<"prod" | "stage">("prod");
  const [kind, setKind] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    const params: Record<string, string> = { env, limit: "200" };
    if (kind) params.kind = kind;
    if (status) params.status = status;
    api
      .signals(params)
      .then((d) => {
        setSignals((d.signals as Signal[]) ?? []);
        setSummary((d as { summary?: SignalsSummary }).summary ?? null);
        setErr(null);
      })
      .catch((e) => setErr(String(e)));
  };

  useEffect(() => {
    reload();
    const t = setInterval(reload, 60_000);
    const unsub = wsClient.subscribe((e) => {
      if (e.type === "signals.update") reload();
    });
    return () => {
      clearInterval(t);
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, kind, status]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Сигналы</h1>
          <p className="text-sm text-panel-muted">
            Ошибки и пожелания от агентов/пользователей Coagent.
            {summary && summary.total !== undefined && (
              <>
                {" · "}всего {summary.total} · новых {summary.newCount ?? 0} · в работе{" "}
                {summary.inProgressCount ?? 0} · закрытых {summary.resolvedCount ?? 0}
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="rounded border border-panel-border bg-panel-bg px-2 py-1 text-xs"
          >
            <option value="">все типы</option>
            <option value="bug">bug</option>
            <option value="request">request</option>
            <option value="incident">incident</option>
            <option value="agent_failure">agent_failure</option>
            <option value="integration_failure">integration_failure</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded border border-panel-border bg-panel-bg px-2 py-1 text-xs"
          >
            <option value="">все статусы</option>
            <option value="new">new</option>
            <option value="triaged">triaged</option>
            <option value="in_progress">in_progress</option>
            <option value="resolved">resolved</option>
            <option value="dismissed">dismissed</option>
          </select>
          {(["prod", "stage"] as const).map((e) => (
            <button
              key={e}
              onClick={() => setEnv(e)}
              className={"btn text-xs " + (env === e ? "btn-primary" : "")}
            >
              {e}
            </button>
          ))}
        </div>
      </header>

      {err && <div className="card pill-err border-panel-err">Ошибка: {err}</div>}

      <div className="space-y-2">
        {signals.map((s) => (
          <div key={s.id} className="card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`pill text-[10px] ${SEV_CLASS[s.severity] ?? ""}`}>
                    {s.severity}
                  </span>
                  <span className={`pill text-[10px] ${STATUS_CLASS[s.status] ?? ""}`}>
                    {s.status}
                  </span>
                  <span className="pill text-[10px]">{s.kind}</span>
                  {s.occurrenceCount > 1 && (
                    <span className="pill text-[10px] pill-warn">×{s.occurrenceCount}</span>
                  )}
                  <div className="flex-1 truncate font-medium">{s.title}</div>
                </div>
                {s.description && (
                  <div className="mt-1 line-clamp-2 text-xs text-panel-muted">{s.description}</div>
                )}
                <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-panel-muted">
                  {s.companyName && <span>🏢 {s.companyName}</span>}
                  {s.templateName && <span>🤖 {s.templateName}</span>}
                  {s.reportedByUserName && <span>👤 {s.reportedByUserName}</span>}
                  {s.sourceKind && <span>src: {s.sourceKind}</span>}
                  {s.lastSeenAt && <span>last: {new Date(s.lastSeenAt).toLocaleString()}</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
        {signals.length === 0 && (
          <div className="card text-xs text-panel-muted">
            Сигналов нет. Либо ничего не сломалось (хорошо!), либо COAGENT_ADMIN_TOKEN не настроен — задай в `.env`.
          </div>
        )}
      </div>
    </div>
  );
}
